import { useEffect, useState } from 'react';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { ERRORS } from '@/constants';
import { ApiError } from '@/services/api/client';
import { useRevealLicenceKey, useSettings } from '@/services/queries';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

/**
 * The licence card's "Reveal key" column.
 *
 * As the design specifies: the key is masked until the person re-enters their
 * password, then shown in full for 60 seconds, and the server logs the reveal.
 * The password step follows the "Two-factor authentication" switch in
 * Settings - on by default; when the person has turned it off, Reveal key
 * fetches the key straight away.
 *
 * The full key is never in the licence response - only `licenseKeyMasked`
 * (`NUDENT-02-•••••-•••••`). It exists on this page only while revealed, in
 * this component's state, and is dropped when the timer runs out, the person
 * hides it, or the component unmounts. Deliberately not cached anywhere.
 */
export function LicenceKeyReveal({ masked }: { masked: string | undefined }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [revealed, setRevealed] = useState<{ key: string; until: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // Assume the check is on until Settings says otherwise - the safe default.
  const needsPassword = useSettings().data?.revealRequiresPassword ?? true;
  const direct = useRevealLicenceKey();

  const show = (key: string, seconds: number) => {
    const start = Date.now();
    setDialogOpen(false);
    setNow(start);
    setRevealed({ key, until: start + seconds * 1000 });
  };

  const reveal = () => {
    if (needsPassword) {
      setDialogOpen(true);
      return;
    }
    direct.mutate(undefined, {
      onSuccess: (result) => show(result.licenseKey, result.expiresInSeconds),
      onError: (error) => {
        // Switched back on elsewhere since this page loaded: ask after all.
        if (error instanceof ApiError && error.code === 'password_incorrect') setDialogOpen(true);
        else toast.error('Could not reveal the key. Try again in a moment.');
      },
    });
  };

  /*
   * Counted against a deadline rather than decremented, so a throttled
   * background tab cannot stretch 60 seconds into several minutes. At the
   * deadline the key is dropped from state, not merely hidden.
   */
  useEffect(() => {
    if (!revealed) return;
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= revealed.until) setRevealed(null);
    }, 250);
    return () => window.clearInterval(timer);
  }, [revealed]);

  const secondsLeft = revealed ? Math.max(0, Math.ceil((revealed.until - now) / 1000)) : 0;

  if (!masked) {
    // Somebody with no seat of their own - a lab owner who administers without
    // designing. There is no key to show and no failure to report.
    return (
      <div className="min-w-0 sm:border-l sm:border-white/30 sm:pl-[26px]">
        <div className="mb-2 text-[13.5px] text-white/70">Licence key</div>
        <span className="text-[12.5px] text-white/70">No seat assigned to you</span>
      </div>
    );
  }

  const clock = `${String(Math.floor(secondsLeft / 60))}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return (
    <div className="min-w-0 sm:border-l sm:border-white/30 sm:pl-[26px]">
      <button
        type="button"
        onClick={() => (revealed ? setRevealed(null) : reveal())}
        disabled={direct.isPending}
        className="mb-2 flex items-center gap-1.5 text-left text-[13.5px] text-white/70 hover:cursor-pointer hover:text-white"
      >
        {revealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
        {revealed ? 'Hide key' : 'Reveal key'}
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-block rounded-[7px] border border-white/20 bg-white/20 px-3 py-[7px] font-mono text-[12.5px] break-all text-white">
          {revealed ? revealed.key : masked}
        </span>
        {revealed && (
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(revealed.key).then(
                () => toast.success('Licence key copied'),
                () => toast.error('Could not copy - select the key and copy it instead.'),
              );
            }}
            aria-label="Copy licence key"
            className="rounded-[7px] border border-white/20 bg-white/10 p-[7px] text-white/80 hover:cursor-pointer hover:bg-white/20 hover:text-white"
          >
            <Copy className="size-3.5" />
          </button>
        )}
      </div>

      {revealed && (
        <div className="mt-1.5 text-[11.5px] text-white/65" aria-live="polite">
          Hides in <span className="font-mono font-semibold text-white">{clock}</span>
        </div>
      )}

      <RevealKeyDialog open={dialogOpen} onOpenChange={setDialogOpen} onRevealed={show} />
    </div>
  );
}

/** Password re-entry. Opens empty every time; the password is never kept. */
function RevealKeyDialog({
  open,
  onOpenChange,
  onRevealed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRevealed: (key: string, seconds: number) => void;
}) {
  const reveal = useRevealLicenceKey();
  const [password, setPassword] = useState('');

  const close = (next: boolean) => {
    if (!next) {
      setPassword('');
      reveal.reset();
    }
    onOpenChange(next);
  };

  const apiError = reveal.error instanceof ApiError ? reveal.error : null;
  const message = !apiError
    ? null
    : apiError.code === 'password_incorrect'
      ? ERRORS.passwordIncorrect
      : apiError.code === 'account_locked'
        ? ERRORS.accountLocked
        : apiError.code === 'rate_limited'
          ? ERRORS.rateLimited
          : apiError.message;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <form
          noValidate
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!password) return;
            reveal.mutate(password, {
              onSuccess: (result) => {
                setPassword('');
                reveal.reset();
                onRevealed(result.licenseKey, result.expiresInSeconds);
              },
              onError: () => setPassword(''),
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>Reveal licence key</DialogTitle>
            <DialogDescription>
              Enter your password to show your full licence key. It is shown for 60 seconds, and the
              reveal is recorded.
            </DialogDescription>
          </DialogHeader>

          {message && (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {message}
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="reveal-password" className="text-xs font-semibold text-[#48536A]">
              Password
            </Label>
            <Input
              id="reveal-password"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="h-[42px] rounded-[9px] border-[1.5px] px-[13px]"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!password || reveal.isPending}>
              {reveal.isPending ? 'Checking…' : 'Reveal key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
